// import libraries
import { createKeyedResourceSlice } from '../../lib/resourceSlice';
import { fetchUserRecordById, subscribeUserRecordById } from './api';

export const SLICE_NAME = 'userRecord';

export const createUserRecordSlice = (set, get) => ({
  [SLICE_NAME]: {
    ...createKeyedResourceSlice({
      name: SLICE_NAME,
      fetchById: async (id) => {
        return fetchUserRecordById(id);
      },
      subscribeById: (id, onData, onError) => {
        return subscribeUserRecordById(id, onData, onError);
      },
      defaultTTL: 5 * 60 * 1000, // 5 minutes cache
    })(set, get)[SLICE_NAME],

    // one-time init guard
    hasInitializedFromLocalStorage: false,

    initializeFromLocalStorage: () => {
      const state = get();
      if (state[SLICE_NAME]?.hasInitializedFromLocalStorage) return;

      const userId = state.common?.auth?.session?.uid;
      if (!userId) return;

      const cacheKey = `userRecord_${userId}`;
      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const data = JSON.parse(cachedData);
          set({
            [SLICE_NAME]: {
              ...state[SLICE_NAME],
              hasInitializedFromLocalStorage: true,
              items: {
                ...state[SLICE_NAME]?.items,
                [userId]: {
                  data,
                  isLoading: false,
                  error: null,
                  cachedAt: Date.now()
                }
              }
            }
          });
        } else {
          set({
            [SLICE_NAME]: {
              ...state[SLICE_NAME],
              hasInitializedFromLocalStorage: true,
            }
          });
        }
      } catch (error) {
        set({
          [SLICE_NAME]: {
            ...state[SLICE_NAME],
            hasInitializedFromLocalStorage: true,
          }
        });
      }
    },
    
    // Clear cache for a specific user
    clearCache: (userId) => {
      const cacheKey = `userRecord_${userId}`;
      try {
        localStorage.removeItem(cacheKey);
      } catch (error) {
        // swallow
      }
    },
    
    // Clear all user record caches
    clearAllCaches: () => {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('userRecord_')) {
          localStorage.removeItem(key);
        }
      });
    },
  },
});

// Selectors

export const selectCurrentUserRecord = (state) => {
  const userId = state.common?.auth?.session?.uid;
  if (!userId) return null;
  const slice = selectUserRecordSlice(state);
  return slice?.items?.[userId]?.data || null;
};

export const selectUserRecordData = (state) => {
  const slice = selectUserRecordSlice(state);
  return slice?.items || {};
};

export const selectUserRecordSlice = (state) => {
  const slice = state.common?.[SLICE_NAME] || state[SLICE_NAME];

  return slice;
};

export const selectUserRecordById = (userId) => (state) => {
  const slice = selectUserRecordSlice(state);

  return slice?.items?.[userId]?.data;
};

export const selectUserRecordFlagsById = (userId) => (state) => {
  const slice = selectUserRecordSlice(state);
  const item = slice?.items?.[userId] || {};
  return {
    isLoading: !!item.isLoading,
    isPrefetching: !!item.isPrefetching,
    hasLiveSubscription: !!item.hasLiveSubscription,
    error: item.error || null,
    cachedAt: item.cachedAt,
  };
};
